import { useState, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { getAlternateLanguageUrl, type Language } from '@/lib/routes';
import { navigateWithTransition } from '@/components/common/RouteTransition';

/**
 * Both languages in one pill, a white thumb behind the current one. Choosing
 * the other slides the thumb across (the colours swap with it), then the page
 * panel rises with the language's name and the page changes beneath it.
 *
 * The options are real `<a href hrefLang>` links that are always mounted, so
 * the prerendered HTML carries the alternate-language link for crawlers.
 * Drawn in white for the navbar's difference blend and the ink menu.
 */
const languages: { code: Language; name: string; full: string }[] = [
  { code: 'ro', name: 'RO', full: 'Română' },
  { code: 'ru', name: 'RU', full: 'Русский' },
];

const SLIDE_MS = 260;

const LanguageSwitcher = ({ className = '' }: { className?: string }) => {
  const { language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [target, setTarget] = useState<Language | null>(null);
  const shown = target ?? language;
  const index = Math.max(0, languages.findIndex((l) => l.code === shown));

  const choose = (e: MouseEvent<HTMLAnchorElement>, code: Language, full: string, href: string) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    if (code === language || target) return;
    setTarget(code);
    // The thumb slides, then the page panel carries the change; without
    // transitions (reduced motion) it is a plain navigation.
    window.setTimeout(() => {
      if (!navigateWithTransition(href, { replace: true, label: full })) navigate(href, { replace: true });
    }, SLIDE_MS);
  };

  return (
    <div
      role="group"
      aria-label="Language"
      className={`label-mono relative grid h-11 shrink-0 grid-cols-2 rounded-byte border border-white p-1 ${className}`}
    >
      <span
        aria-hidden="true"
        className="absolute bottom-1 left-1 top-1 w-[calc(50%-4px)] rounded-[2px] bg-white transition-transform ease-[cubic-bezier(0.76,0,0.24,1)]"
        style={{ transform: `translateX(${index * 100}%)`, transitionDuration: `${SLIDE_MS + 80}ms` }}
      />
      {languages.map((lang) => {
        const href = getAlternateLanguageUrl(location.pathname, lang.code);
        const on = lang.code === shown;
        return (
          <Link
            key={lang.code}
            to={href}
            replace
            hrefLang={lang.code}
            aria-current={lang.code === language ? 'true' : undefined}
            onClick={(e) => choose(e, lang.code, lang.full, href)}
            className={`relative z-10 flex min-w-[2.75rem] items-center justify-center rounded-[2px] px-2.5 transition-colors duration-300 ${
              on ? 'text-ink' : 'text-white/80 hover:text-white'
            }`}
          >
            {lang.name}
          </Link>
        );
      })}
    </div>
  );
};

export default LanguageSwitcher;
